import { defineStore } from 'pinia'
import { ref } from 'vue'
import { writeArticle as writeArticleOpenAI } from '../services/openai'
import { useNotificationStore } from './notification'
import { useArticleStore } from './article'
import { imageService } from '../services/imageService'
import { supabase } from '../services/supabase'

export const useOpenAIStore = defineStore('openai', () => {
  const loading = ref(false)
  const error = ref(null)
  const notificationStore = useNotificationStore()
  const articleStore = useArticleStore()

  async function writeArticle(params) {
    try {
      loading.value = true
      error.value = null

      const { 
        keyword, 
        title, 
        keyPoints = [], 
        searchVolume, 
        competition, 
        competitionLevel, 
        seed_keyword,
        language_code,
        searchIntent,
        includeFaqs = false,
        includeTakeaways = false,
        numFaqs = 0,
        numTakeaways = 0
      } = params

      if (!language_code) {
        console.error('No language code provided to store:', params)
        throw new Error('Language code is required')
      }

      // Validate FAQ parameters
      const parsedNumFaqs = parseInt(numFaqs, 10);
      const parsedNumTakeaways = parseInt(numTakeaways, 10);

      console.log('Store writing article with:', { 
        keyword, 
        title,
        language_code,
        seed_keyword,
        searchIntent,
        includeFaqs,
        numFaqs: parsedNumFaqs,
        includeTakeaways,
        numTakeaways: parsedNumTakeaways
      })

      notificationStore.addNotification({
        type: 'info',
        message: 'Generating article content... This may take a minute. Feel free to close the pop-up',
        duration: 10000
      })

      let effectiveSearchIntent = searchIntent

      try {
        // Fetch the keyword's search intent from the database if not provided
        if (!effectiveSearchIntent?.primary) {
          console.log('Fetching search intent for keyword:', keyword)
          const { data: keywordData, error } = await supabase
            .from('keywords')
            .select('search_intent_primary, search_intent_secondary, language_code')
            .eq('term', keyword)
            .limit(1)  // Limit to 1 result
            .maybeSingle()  // Use maybeSingle() instead of single()
          
          if (error) {
            console.error('Error fetching keyword search intent:', error)
          } else if (keywordData) {
            console.log('Found keyword data:', keywordData)
            effectiveSearchIntent = {
              primary: keywordData.search_intent_primary,
              secondary: keywordData.search_intent_secondary || []
            }
            // Double check language code matches database
            if (keywordData.language_code && keywordData.language_code !== language_code) {
              console.warn('Language code mismatch:', {
                provided: language_code,
                database: keywordData.language_code
              })
            }
          }
        }

        // Log the parameters being sent to OpenAI
        console.log('Sending parameters to OpenAI:', {
          keyword,
          title,
          keyPoints,
          searchVolume,
          competition,
          competitionLevel,
          language_code,
          includeFaqs,
          includeTakeaways,
          numFaqs: parsedNumFaqs,
          numTakeaways: parsedNumTakeaways,
          effectiveSearchIntent
        })

        const content = await writeArticleOpenAI({
          keyword,
          title,
          keyPoints,
          searchVolume,
          competition,
          competitionLevel,
          language_code,
          includeFaqs,
          includeTakeaways,
          numFaqs: parsedNumFaqs,
          numTakeaways: parsedNumTakeaways
        })
        
        console.log('Generated article length:', content.length)
        
        // Get a relevant image using imageService
        let photo = null
        try {
          const searchQuery = title || keyword
          if (searchQuery) {
            const images = await imageService.searchImages(searchQuery, 1)
            photo = images[0]
          }
        } catch (imageError) {
          console.error('Error fetching image:', imageError)
          // Continue without image if there's an error
        }
        
        // Save the article to the database
        await articleStore.saveArticle({
          title,
          content,
          keyword,
          seed_keyword,
          searchVolume,
          competition,
          competitionLevel,
          featuredImage: photo,
          searchIntent: effectiveSearchIntent,
          language_code
        })

        notificationStore.addNotification({
          type: 'success',
          message: 'Article generated and saved successfully!'
        })

        return content

      } catch (error) {
        console.error('Error generating article:', error)
        notificationStore.addNotification({
          type: 'error',
          message: 'Failed to generate article: ' + error.message
        })
        throw error
      }
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    error,
    writeArticle
  }
})
